import { MAP_STATUSES, money, type MapStatus } from "@valuemap/core";
import { useEffect, useState } from "react";
import { fetchMaps, type MapSummary } from "./api.js";
import styles from "./Home.module.css";

function shortDate(date: string | null): string {
  return date
    ? new Date(date + "T00:00").toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
}

/** Staff home: value maps from Salesforce, most recently changed first, with search and filters. */
export function Home() {
  const [result, setResult] = useState<{ key: string; maps: MapSummary[] | "failed" } | null>(null);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<MapStatus | "">("");
  const [mine, setMine] = useState(false);

  // Wait for a pause in typing before asking Salesforce.
  useEffect(() => {
    const t = setTimeout(() => setQ(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const key = `${q}|${status}|${mine ? 1 : 0}`;
  useEffect(() => {
    const ctl = new AbortController();
    fetchMaps({ q, status: status || undefined, mine }, ctl.signal).then(
      (maps) => setResult({ key, maps }),
      (err: unknown) => {
        if ((err as Error).name !== "AbortError") setResult({ key, maps: "failed" });
      },
    );
    return () => ctl.abort();
  }, [key, q, status, mine]);

  // Keep showing the last list while a new search loads, so the page does not flicker.
  const failed = result?.maps === "failed" && result.key === key;
  const maps = result && result.maps !== "failed" ? result.maps : null;

  const filtered = q.trim() !== "" || status !== "" || mine;

  return (
    <main className={styles.wrap}>
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>SalesFix staff</div>
          <h1>Value maps</h1>
        </div>
        <a className={styles.newBtn} href="/new">
          New value map
        </a>
      </div>

      <form className={styles.filters} role="search" onSubmit={(e) => e.preventDefault()}>
        <label className={styles.filter}>
          <span>Search</span>
          <input
            type="search"
            placeholder="Title or Account name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className={styles.filter}>
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as MapStatus | "")}>
            <option value="">All except archived</option>
            {MAP_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          <span>My maps</span>
        </label>
      </form>

      {failed && (
        <div className={styles.alert} role="alert">
          Value maps could not be loaded from Salesforce. Please refresh the page.
        </div>
      )}
      {!failed && maps === null && <p className={styles.note}>Loading…</p>}
      {maps && maps.length === 0 && (
        <p className={styles.note}>
          {filtered ? "No value maps match those filters." : "No value maps yet."}
        </p>
      )}

      {maps && maps.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Value map</th>
              <th scope="col">Account</th>
              <th scope="col">Status</th>
              <th scope="col">Workshop</th>
              <th scope="col" className={styles.num}>
                Year 1
              </th>
              <th scope="col" className={styles.num}>
                Total
              </th>
              <th scope="col">Owner</th>
            </tr>
          </thead>
          <tbody>
            {maps.map((m) => (
              <tr key={m.id}>
                <td>
                  <a href={`/maps/${encodeURIComponent(m.id)}`} className={styles.title}>
                    {m.title || "Untitled value map"}
                  </a>
                  <span className={styles.ref}>{m.reference}</span>
                </td>
                <td>{m.accountName ?? ""}</td>
                <td>
                  <span className={styles.status}>{m.status}</span>
                </td>
                <td>{shortDate(m.workshopDate)}</td>
                <td className={styles.num}>{m.totalYear1 != null ? money(m.totalYear1) : ""}</td>
                <td className={styles.num}>
                  {m.totalExpected != null ? money(m.totalExpected) : ""}
                  {m.horizon ? <span className={styles.ref}>{m.horizon}-year</span> : null}
                </td>
                <td>{m.ownerName ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
